// src/components/admin/sections/AdminActivities.tsx - VERSIÓN FINAL COMPLETA
import React, { useState, useEffect, useRef } from 'react';
import apiElCristo, { Activity, ActivitiesData } from '../../../service/api_elcristo';
import './AdminActivities.css';

const AdminActivities: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<{ activity: Activity, index: number } | null>(null);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [uploadingImage, setUploadingImage] = useState<'main' | 'shape' | null>(null);
  
  const titleEditorRef = useRef<HTMLDivElement>(null);
  const descriptionEditorRef = useRef<HTMLDivElement>(null);
  const activityDescEditorRef = useRef<HTMLDivElement>(null);

  const [activitiesData, setActivitiesData] = useState<ActivitiesData>({
    subtitle: '',
    title: '',
    description: '',
    wordsbutton: '',
    wordslink: '',
    image: '',
    radiusShape: '',
    activities: []
  });

  const [formData, setFormData] = useState<Activity>({
    icon: '',
    title: '',
    description: '',
    iconColor: 'box-color-1'
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (titleEditorRef.current && activitiesData.title) {
      titleEditorRef.current.innerHTML = activitiesData.title;
    }
    if (descriptionEditorRef.current && activitiesData.description) {
      descriptionEditorRef.current.innerHTML = activitiesData.description;
    }
  }, [activitiesData.title, activitiesData.description]);

  useEffect(() => {
    if (activityDescEditorRef.current && formData.description && showModal) {
      activityDescEditorRef.current.innerHTML = formData.description;
    }
  }, [showModal]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getActivitiesData();
      console.log('📥 Activities data:', data);
      setActivitiesData(data);
    } catch (error) {
      console.error('❌ Error loading:', error);
      setMessage({ type: 'error', text: 'Error al cargar actividades' });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setActivitiesData(prev => ({ ...prev, [name]: value }));
  };

  const handleTitleInput = () => {
    if (titleEditorRef.current) {
      setActivitiesData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
    }
  };

  const handleDescriptionInput = () => {
    if (descriptionEditorRef.current) {
      setActivitiesData(prev => ({ ...prev, description: descriptionEditorRef.current!.innerHTML }));
    }
  };

  const handleActivityDescInput = () => {
    if (activityDescEditorRef.current) {
      setFormData(prev => ({ ...prev, description: activityDescEditorRef.current!.innerHTML }));
    }
  };

  const applyFormat = (editorRef: React.RefObject<HTMLDivElement>, tag: string) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (selectedText) {
      const span = document.createElement('span');
      span.className = tag;
      span.textContent = selectedText;
      range.deleteContents();
      range.insertNode(span);
      
      if (editorRef === titleEditorRef) {
        setActivitiesData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
      } else if (editorRef === descriptionEditorRef) {
        setActivitiesData(prev => ({ ...prev, description: descriptionEditorRef.current!.innerHTML }));
      } else if (editorRef === activityDescEditorRef) {
        setFormData(prev => ({ ...prev, description: activityDescEditorRef.current!.innerHTML }));
      }
    }
  };

  const clearFormat = (editorRef: React.RefObject<HTMLDivElement>) => {
    if (!editorRef.current) return;
    
    const plainText = editorRef.current.innerText;
    editorRef.current.innerHTML = plainText;
    
    if (editorRef === titleEditorRef) {
      setActivitiesData(prev => ({ ...prev, title: plainText }));
    } else if (editorRef === descriptionEditorRef) {
      setActivitiesData(prev => ({ ...prev, description: plainText }));
    } else if (editorRef === activityDescEditorRef) {
      setFormData(prev => ({ ...prev, description: plainText }));
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'main' | 'shape') => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: '❌ Solo PNG, JPG o WEBP' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '❌ Máximo 5MB' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      setUploadingImage(type);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('folder', 'activities');
      
      const response = await fetch('https://backend.anubisai.net/api/elcristo/admin/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('elcristo_token')}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al subir');
      }
      
      const data = await response.json();
      
      if (type === 'main') {
        setActivitiesData(prev => ({ ...prev, image: data.url }));
      } else {
        setActivitiesData(prev => ({ ...prev, radiusShape: data.url }));
      }
      
      setMessage({ type: 'success', text: '✅ Imagen subida' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: `❌ ${err.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUploadingImage(null);
    }
  };

  const handleOpenModal = (activity?: Activity, index?: number) => {
    if (activity && index !== undefined) {
      setEditingActivity(activity);
      setEditingIndex(index);
      setFormData({ ...activity });
    } else {
      setEditingActivity(null);
      setEditingIndex(null);
      setFormData({
        icon: '',
        title: '',
        description: '',
        iconColor: 'box-color-1'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingActivity(null);
    setEditingIndex(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveActivity = async () => {
    if (!formData.icon || !formData.title || !formData.description) {
      setMessage({ type: 'error', text: '❌ Completa todos los campos' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setSaving(true);

    try {
      let updatedActivities;
      
      if (editingIndex !== null) {
        updatedActivities = [...activitiesData.activities];
        updatedActivities[editingIndex] = formData;
      } else {
        updatedActivities = [...activitiesData.activities, formData];
      }

      const updatedData = { ...activitiesData, activities: updatedActivities };
      
      console.log('📤 Guardando actividad:', updatedData);
      
      await apiElCristo.updateSiteContent({ activities: updatedData });
      
      setActivitiesData(updatedData);
      handleCloseModal();
      
      setMessage({ type: 'success', text: '✅ Actividad guardada' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteModal = (activity: Activity, index: number) => {
    setActivityToDelete({ activity, index });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setActivityToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!activityToDelete) return;

    setSaving(true);

    try {
      const updatedActivities = activitiesData.activities.filter((_, i) => i !== activityToDelete.index);
      const updatedData = { ...activitiesData, activities: updatedActivities };
      
      console.log('📤 Eliminando actividad:', updatedData);
      
      await apiElCristo.updateSiteContent({ activities: updatedData });
      
      setActivitiesData(updatedData);
      handleCloseDeleteModal();
      
      setMessage({ type: 'success', text: '✅ Actividad eliminada' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveActivity = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= activitiesData.activities.length) return;

    const activities = [...activitiesData.activities];
    [activities[index], activities[newIndex]] = [activities[newIndex], activities[index]];
    setActivitiesData(prev => ({ ...prev, activities }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      console.log('📤 Guardando cambios generales:', activitiesData);
      
      await apiElCristo.updateSiteContent({ activities: activitiesData });
      
      setMessage({ type: 'success', text: '✅ Cambios guardados' });
      setTimeout(() => setMessage(null), 3000);
      
      await loadData();
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
    } finally {
      setSaving(false);
    }
  };

  const getColorClass = (color: string) => {
    switch (color) {
      case 'box-color-1': return 'color-blue';
      case 'box-color-2': return 'color-orange';
      case 'box-color-3': return 'color-green';
      case 'box-color-4': return 'color-purple';
      default: return 'color-blue';
    }
  };

  if (loading) {
    return (
      <div className="admin-activities-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-activities-container">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-running"></i> Actividades Escolares</h1>
          <p>Administrar actividades y programas</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
          <i className="fas fa-plus"></i>
          Nueva Actividad
        </button>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check' : 'exclamation'}-circle`}></i>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-cog"></i> Configuración</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label><i className="fas fa-tag"></i> Subtítulo</label>
              <input
                type="text"
                name="subtitle"
                value={activitiesData.subtitle}
                onChange={handleSectionChange}
                placeholder="Ej: Nuestras Actividades"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label><i className="fas fa-heading"></i> Título</label>
              
              <div className="editor-toolbar">
                <button type="button" className="toolbar-btn" onClick={() => applyFormat(titleEditorRef, 'elcristo-primary')}>
                  <i className="fas fa-font" style={{ color: '#ff0f00' }}></i> Rojo
                </button>
                <button type="button" className="toolbar-btn" onClick={() => applyFormat(titleEditorRef, 'elcristo-blue')}>
                  <i className="fas fa-font" style={{ color: '#70A6B1' }}></i> Azul
                </button>
                <button type="button" className="toolbar-btn" onClick={() => clearFormat(titleEditorRef)}>
                  <i className="fas fa-eraser"></i> Limpiar
                </button>
              </div>

              <div
                ref={titleEditorRef}
                className="rich-editor"
                contentEditable
                onInput={handleTitleInput}
                suppressContentEditableWarning
                data-placeholder="Título de la sección"
              />
            </div>

            <div className="form-group">
              <label><i className="fas fa-align-left"></i> Descripción</label>
              
              <div className="editor-toolbar">
                <button type="button" className="toolbar-btn" onClick={() => applyFormat(descriptionEditorRef, 'elcristo-primary')}>
                  <i className="fas fa-font" style={{ color: '#ff0f00' }}></i> Rojo
                </button>
                <button type="button" className="toolbar-btn" onClick={() => applyFormat(descriptionEditorRef, 'elcristo-blue')}>
                  <i className="fas fa-font" style={{ color: '#70A6B1' }}></i> Azul
                </button>
                <button type="button" className="toolbar-btn" onClick={() => clearFormat(descriptionEditorRef)}>
                  <i className="fas fa-eraser"></i> Limpiar
                </button>
              </div>

              <div
                ref={descriptionEditorRef}
                className="rich-editor rich-editor-large"
                contentEditable
                onInput={handleDescriptionInput}
                suppressContentEditableWarning
                data-placeholder="Descripción de actividades"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label><i className="fas fa-mouse-pointer"></i> Texto Botón</label>
                <input
                  type="text"
                  name="wordsbutton"
                  value={activitiesData.wordsbutton}
                  onChange={handleSectionChange}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label><i className="fas fa-link"></i> Link Botón</label>
                <input
                  type="text"
                  name="wordslink"
                  value={activitiesData.wordslink}
                  onChange={handleSectionChange}
                  className="form-control"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-images"></i> Imágenes</h3>
          </div>
          <div className="card-body">
            <div className="images-grid">
              <div className="image-upload-card">
                <div className="image-upload-header">
                  <i className="fas fa-image"></i>
                  <h4>Imagen Principal</h4>
                </div>
                
                {activitiesData.image && (
                  <div className="image-preview-large">
                    <img 
                      src={activitiesData.image.startsWith('http') 
                        ? activitiesData.image 
                        : `${window.location.origin}${activitiesData.image}`
                      } 
                      alt="Main" 
                    />
                  </div>
                )}
                
                <input
                  type="text"
                  value={activitiesData.image}
                  onChange={(e) => setActivitiesData(prev => ({ ...prev, image: e.target.value }))}
                  className="form-control"
                  style={{ marginBottom: '10px' }}
                />
                
                <button
                  type="button"
                  className="btn btn-upload-full"
                  disabled={uploadingImage === 'main'}
                  onClick={() => document.getElementById('main-img')?.click()}
                >
                  {uploadingImage === 'main' ? (
                    <><div className="spinner-small"></div> Subiendo...</>
                  ) : (
                    <><i className="fas fa-upload"></i> Subir</>
                  )}
                </button>
                <input
                  id="main-img"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'main')}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="image-upload-card">
                <div className="image-upload-header">
                  <i className="fas fa-circle"></i>
                  <h4>Forma Decorativa</h4>
                </div>
                
                {activitiesData.radiusShape && (
                  <div className="image-preview-large">
                    <img 
                      src={activitiesData.radiusShape.startsWith('http') 
                        ? activitiesData.radiusShape 
                        : `${window.location.origin}${activitiesData.radiusShape}`
                      } 
                      alt="Shape" 
                    />
                  </div>
                )}
                
                <input
                  type="text"
                  value={activitiesData.radiusShape}
                  onChange={(e) => setActivitiesData(prev => ({ ...prev, radiusShape: e.target.value }))}
                  className="form-control"
                  style={{ marginBottom: '10px' }}
                />
                
                <button
                  type="button"
                  className="btn btn-upload-full"
                  disabled={uploadingImage === 'shape'}
                  onClick={() => document.getElementById('shape-img')?.click()}
                >
                  {uploadingImage === 'shape' ? (
                    <><div className="spinner-small"></div> Subiendo...</>
                  ) : (
                    <><i className="fas fa-upload"></i> Subir</>
                  )}
                </button>
                <input
                  id="shape-img"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'shape')}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-list"></i> Actividades ({activitiesData.activities.length})</h3>
          </div>
          <div className="card-body">
            {activitiesData.activities.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-running"></i>
                <p>No hay actividades</p>
                <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
                  <i className="fas fa-plus"></i>
                  Crear Actividad
                </button>
              </div>
            ) : (
              <div className="activities-grid">
                {activitiesData.activities.map((activity, index) => (
                  <div key={index} className="activity-card">
                    <div className="activity-card-drag">
                      <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    <div className={`activity-icon ${getColorClass(activity.iconColor || 'box-color-1')}`}>
                      <i className={activity.icon}></i>
                    </div>
                    
                    <div className="activity-content">
                      <h4>{activity.title}</h4>
                      <p dangerouslySetInnerHTML={{ __html: activity.description }}></p>
                      <span className="color-badge">{activity.iconColor}</span>
                    </div>
                    
                    <div className="activity-card-actions">
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => handleMoveActivity(index, 'up')}
                        disabled={index === 0}
                      >
                        <i className="fas fa-arrow-up"></i>
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => handleMoveActivity(index, 'down')}
                        disabled={index === activitiesData.activities.length - 1}
                      >
                        <i className="fas fa-arrow-down"></i>
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-edit"
                        onClick={() => handleOpenModal(activity, index)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-delete"
                        onClick={() => handleOpenDeleteModal(activity, index)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={loadData} disabled={saving}>
            <i className="fas fa-undo"></i> Recargar
          </button>

          <button type="submit" className="btn btn-primary" disabled={saving || uploadingImage !== null}>
            {saving ? (
              <><div className="spinner-small"></div> Guardando...</>
            ) : (
              <><i className="fas fa-save"></i> Guardar Cambios</>
            )}
          </button>
        </div>
      </form>

      {/* PREVIEW SECTION */}
      <div className="activities-preview-section">
        <div className="activities-preview-header">
          <h3><i className="fas fa-eye"></i> Vista Previa</h3>
        </div>
        
        <div className="activities-preview">
          <div className="activities-preview-image">
            {activitiesData.image && (
              <div className="preview-image-main">
                <img 
                  src={activitiesData.image.startsWith('http') 
                    ? activitiesData.image 
                    : `${window.location.origin}${activitiesData.image}`
                  } 
                  alt="Activities" 
                />
              </div>
            )}
            
            {activitiesData.radiusShape && (
              <div className="preview-radius-shape">
                <img 
                  src={activitiesData.radiusShape.startsWith('http') 
                    ? activitiesData.radiusShape 
                    : `${window.location.origin}${activitiesData.radiusShape}`
                  } 
                  alt="Shape" 
                />
              </div>
            )}
            
            {activitiesData.wordsbutton && (
              <div className="preview-button-overlay">
                <button className="preview-theme-btn">
                  {activitiesData.wordsbutton}
                </button>
              </div>
            )}
          </div>

          <div className="activities-preview-content">
            <div className="preview-section-title">
              <span className="preview-subtitle">{activitiesData.subtitle || 'Subtítulo'}</span>
              <h2 
                className="preview-title"
                dangerouslySetInnerHTML={{ 
                  __html: activitiesData.title || 'Título Principal' 
                }}
              />
            </div>
            
            <p 
              className="preview-description"
              dangerouslySetInnerHTML={{ 
                __html: activitiesData.description || 'Descripción de las actividades...' 
              }}
            />

            <div className="preview-activities-grid">
              {(activitiesData.activities.length > 0 ? activitiesData.activities : [
                { icon: 'fas fa-futbol', title: 'Deportes', description: 'Actividades deportivas', iconColor: 'box-color-1' },
                { icon: 'fas fa-music', title: 'Música', description: 'Clases de música', iconColor: 'box-color-2' },
                { icon: 'fas fa-paint-brush', title: 'Arte', description: 'Talleres artísticos', iconColor: 'box-color-3' },
                { icon: 'fas fa-book', title: 'Lectura', description: 'Club de lectura', iconColor: 'box-color-4' }
              ]).map((activity, index) => (
                <div key={index} className="preview-activity-item">
                  <div className={`preview-activity-icon ${getColorClass(activity.iconColor || 'box-color-1')}`}>
                    <i className={activity.icon}></i>
                  </div>
                  <div className="preview-activity-content">
                    <h5>{activity.title}</h5>
                    <p dangerouslySetInnerHTML={{ __html: activity.description }}></p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL CREAR/EDITAR */}
      {showModal && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '650px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-running"></i>
                {editingActivity ? 'Editar Actividad' : 'Nueva Actividad'}
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <div className="form-group">
                <label>Título *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ej: Deportes"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Descripción *</label>
                
                <div className="editor-toolbar">
                  <button type="button" className="toolbar-btn" onClick={() => applyFormat(activityDescEditorRef, 'elcristo-primary')}>
                    <i className="fas fa-font" style={{ color: '#ff0f00' }}></i> Rojo
                  </button>
                  <button type="button" className="toolbar-btn" onClick={() => applyFormat(activityDescEditorRef, 'elcristo-blue')}>
                    <i className="fas fa-font" style={{ color: '#70A6B1' }}></i> Azul
                  </button>
                  <button type="button" className="toolbar-btn" onClick={() => clearFormat(activityDescEditorRef)}>
                    <i className="fas fa-eraser"></i> Limpiar
                  </button>
                </div>

                <div
                  ref={activityDescEditorRef}
                  className="rich-editor"
                  contentEditable
                  onInput={handleActivityDescInput}
                  suppressContentEditableWarning
                  data-placeholder="Descripción de la actividad"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Icono FontAwesome *</label>
                  <input
                    type="text"
                    name="icon"
                    value={formData.icon}
                    onChange={handleChange}
                    placeholder="fas fa-futbol"
                    className="form-control"
                  />
                  <small className="hint">
                    <a href="https://fontawesome.com/icons" target="_blank" rel="noopener noreferrer">
                      Ver iconos
                    </a>
                  </small>
                </div>

                <div className="form-group">
                  <label>Color *</label>
                  <select name="iconColor" value={formData.iconColor} onChange={handleChange} className="form-control">
                    <option value="box-color-1">Azul</option>
                    <option value="box-color-2">Naranja</option>
                    <option value="box-color-3">Verde</option>
                    <option value="box-color-4">Morado</option>
                  </select>
                </div>
              </div>

              {formData.icon && (
                <div className="icon-preview-modal">
                  <label>Preview:</label>
                  <div className={`preview-icon ${getColorClass(formData.iconColor)}`}>
                    <i className={formData.icon}></i>
                  </div>
                </div>
              )}
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveActivity} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Guardando...</>
                ) : (
                  <>{editingActivity ? 'Actualizar' : 'Agregar'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DELETE */}
      {showDeleteModal && activityToDelete && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '500px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ff0f00' }}></i>
                Confirmar Eliminación
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseDeleteModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                ¿Eliminar actividad <strong>"{activityToDelete.activity.title}"</strong>?
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                No se puede deshacer.
              </p>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseDeleteModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Eliminando...</>
                ) : (
                  <><i className="fas fa-trash"></i> Eliminar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminActivities;